import { receiptLines } from "@/lib/pos";

const PRINTER_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ffe0-0000-1000-8000-00805f9b34fb",
];

let cached = { device: null, characteristic: null };

export const bluetoothSupported = () =>
  typeof navigator !== "undefined" && !!navigator.bluetooth;

async function findWritable(server) {
  const services = await server.getPrimaryServices();
  for (const service of services) {
    let chars = [];
    try {
      chars = await service.getCharacteristics();
    } catch (_e) {
      continue;
    }
    const writable = chars.find(
      (c) => c.properties.write || c.properties.writeWithoutResponse,
    );
    if (writable) return writable;
  }
  throw new Error("No writable characteristic found on this printer");
}

export async function connectPrinter() {
  if (!bluetoothSupported()) throw new Error("Web Bluetooth is not supported in this browser");
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: PRINTER_SERVICES,
  });
  const server = await device.gatt.connect();
  const characteristic = await findWritable(server);
  cached = { device, characteristic };
  device.addEventListener("gattserverdisconnected", () => {
    cached = { device: null, characteristic: null };
  });
  return device.name || "Thermal printer";
}

export const connectedPrinterName = () => cached.device?.name || null;

function encode(txn) {
  const bytes = [];
  const push = (...b) => bytes.push(...b);
  const text = (s) => {
    for (const ch of s) {
      const code = ch.charCodeAt(0);
      bytes.push(code < 128 ? code : 63);
    }
  };
  push(0x1b, 0x40); // init
  receiptLines(txn).forEach((row) => {
    push(0x1b, 0x61, row.align === "center" ? 1 : 0);
    push(0x1b, 0x45, row.bold ? 1 : 0);
    const s = row.size === 2 ? 0x11 : 0x00;
    push(0x1d, 0x21, s);
    text(row.text);
    push(0x0a);
  });
  push(0x1b, 0x61, 0);
  push(0x1b, 0x45, 0);
  push(0x1d, 0x21, 0x00);
  push(0x0a, 0x0a, 0x0a);
  push(0x1d, 0x56, 0x42, 0x00); // cut
  return new Uint8Array(bytes);
}

export async function printViaBluetooth(txn) {
  if (!cached.characteristic) await connectPrinter();
  const data = encode(txn);
  const chunk = 180;
  for (let i = 0; i < data.length; i += chunk) {
    const slice = data.slice(i, i + chunk);
    if (cached.characteristic.writeValueWithoutResponse) {
      await cached.characteristic.writeValueWithoutResponse(slice);
    } else {
      await cached.characteristic.writeValue(slice);
    }
    await new Promise((r) => setTimeout(r, 25));
  }
}

/** Fallback: silent iframe print (no popup blockers). */
export function printViaBrowser(txn) {
  const rows = receiptLines(txn)
    .map((r) => {
      const style = [
        "margin:0",
        "white-space:pre",
        `text-align:${r.align === "center" ? "center" : "left"}`,
        r.bold ? "font-weight:800" : "font-weight:500",
        r.size === 2 ? "font-size:15px" : "font-size:12px",
      ].join(";");
      return `<p style="${style}">${r.text.replace(/</g, "&lt;")}</p>`;
    })
    .join("");

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(
    `<html><head><title>${txn.receipt_no || "Receipt"}</title><style>
      @page { size: 80mm auto; margin: 4mm; }
      body { font-family: "Hanken Grotesk", ui-monospace, monospace; color:#000; }
    </style></head><body>${rows}</body></html>`,
  );
  doc.close();
  iframe.contentWindow.focus();
  iframe.contentWindow.print();
  setTimeout(() => iframe.remove(), 1500);
}
